import React from "react";
import {
  Box,
  Typography,
  Button,
  CssBaseline
} from "@mui/material";
import { useHistory } from "react-router-dom";

const PRIVACY_WHITE = "#FFFDF8";
const PRIVACY_GRAY = "#B8B8B8";
const PRIVACY_MUTED = "#777777";

function PrivacyPage() {
  const history = useHistory();

  const sectionTitleSx = {
    fontSize: {
      xs: "1.35rem",
      md: "1.6rem"
    },
    fontWeight: 400,
    letterSpacing: "-0.01em",
    color: PRIVACY_WHITE,
    mb: 2
  };

  const bodySx = {
    fontSize: {
      xs: "0.95rem",
      md: "1rem"
    },
    lineHeight: 1.8,
    fontWeight: 300,
    color: PRIVACY_GRAY,
    mb: 2.5
  };

  return (
    <>
      <CssBaseline />

      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#000",
          color: PRIVACY_WHITE
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            bgcolor: "rgba(0, 0, 0, 0.94)",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid #222",
            px: {
              xs: 3,
              md: 6
            },
            py: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => history.push("/")}
            sx={{
              border: 0,
              background: "transparent",
              padding: 0,
              cursor: "pointer",
              display: "flex",
              alignItems: "center"
            }}
          >
            <Box
              component="img"
              src="/Colloquia_Logo.png"
              alt="Colloquia"
              sx={{
                height: {
                  xs: 55,
                  md: 65
                },
                width: "auto",
                borderRadius: "12px"
              }}
            />
          </Box>

          <Button
            onClick={() => history.push("/")}
            sx={{
              color: PRIVACY_GRAY,
              fontWeight: 300,
              textTransform: "none",
              fontSize: "0.9rem",
              "&:hover": {
                color: PRIVACY_WHITE,
                bgcolor: "transparent"
              }
            }}
          >
            Return to Colloquia
          </Button>
        </Box>

        {/* CONTENT */}
        <Box
          component="main"
          sx={{
            width: "100%",
            maxWidth: 920,
            mx: "auto",
            px: {
              xs: 4,
              sm: 6,
              md: 8
            },
            pt: {
              xs: 8,
              md: 12
            },
            pb: {
              xs: 10,
              md: 14
            }
          }}
        >
          {/* TITLE */}
          <Box sx={{ mb: 8 }}>
            <Typography
              component="h1"
              sx={{
                fontSize: {
                  xs: "2.5rem",
                  sm: "3.2rem",
                  md: "4rem"
                },
                lineHeight: 1.1,
                fontWeight: 300,
                letterSpacing: "-0.035em",
                color: PRIVACY_WHITE,
                mb: 2
              }}
            >
              Privacy Policy
            </Typography>

            <Typography
              sx={{
                fontSize: "0.9rem",
                fontWeight: 300,
                color: PRIVACY_MUTED
              }}
            >
              Last updated: August 22, 2026
            </Typography>
          </Box>

          {/* INTRODUCTION */}
          <Box sx={{ mb: 7 }}>
            <Typography sx={bodySx}>
              Colloquia is a social media platform designed to give
              intellectually substantive ideas and the people who develop
              them greater visibility and influence.
            </Typography>

            <Typography sx={bodySx}>
              We believe that trust is essential to a social platform.
              This Privacy Policy explains what information Colloquia
              collects, how we use it, when we may disclose it, and the
              choices available to you.
            </Typography>

            <Typography sx={bodySx}>
              By using Colloquia, you acknowledge the practices described
              in this Privacy Policy.
            </Typography>
          </Box>

          {/* 1 */}
          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              1. Information We Collect
            </Typography>

            <Typography sx={bodySx}>
              We collect information that you provide directly to us when
              you create an account, use Colloquia, communicate with us,
              or otherwise interact with the platform.
            </Typography>

            <Typography sx={bodySx}>
              Depending on how you use Colloquia, this may include:
            </Typography>

            <Box component="ul" sx={{ pl: 3, mb: 3 }}>
              {[
                "Your name and username.",
                "Your email address.",
                "Your password, stored in a protected form rather than as readable plaintext.",
                "Your educational, professional, or biographical information that you choose to provide.",
                "Information contained in your profile.",
                "Posts, comments, photographs, messages, and other content you submit to Colloquia.",
                "Votes, power votes, reactions, and other interactions with content.",
                "Information you provide when contacting Colloquia support.",
                "Technical information associated with your use of the platform."
              ].map((item, index) => (
                <Typography
                  component="li"
                  key={index}
                  sx={{
                    ...bodySx,
                    mb: 1.25
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>

          {/* 2 */}
          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              2. Educational and Professional Information
            </Typography>

            <Typography sx={bodySx}>
              Colloquia may allow users to provide information concerning
              their education, academic achievements, occupation,
              professional experience, or other intellectual and
              professional accomplishments.
            </Typography>

            <Typography sx={bodySx}>
              Some features of Colloquia may use information that you
              voluntarily provide to help determine eligibility, account
              status, or other platform functionality.
            </Typography>

            <Typography sx={bodySx}>
              You should only provide information that you are comfortable
              associating with your Colloquia account.
            </Typography>
          </Box>

          {/* 3 */}
          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              3. Information Collected Automatically
            </Typography>

            <Typography sx={bodySx}>
              When you access Colloquia, certain technical information may
              be collected automatically. This may include information such
              as your IP address, browser type, device type, operating
              system, approximate location derived from technical
              information, timestamps, and information concerning how you
              interact with the platform.
            </Typography>

            <Typography sx={bodySx}>
              We use this information primarily to operate, secure,
              maintain, troubleshoot, and improve Colloquia.
            </Typography>
          </Box>

          {/* 4 */}
          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              4. How We Use Information
            </Typography>

            <Typography sx={bodySx}>
              We may use information we collect to:
            </Typography>

            <Box component="ul" sx={{ pl: 3, mb: 3 }}>
              {[
                "Create and maintain your account.",
                "Authenticate users and protect accounts.",
                "Provide the features and functionality of Colloquia.",
                "Display profiles and user-generated content.",
                "Operate Colloquia's voting, ranking, popularity, and recommendation systems.",
                "Prevent fraud, abuse, spam, manipulation, and unauthorized access.",
                "Respond to questions, requests, and support inquiries.",
                "Diagnose technical problems and improve platform reliability.",
                "Understand how users interact with Colloquia so that we can improve the product.",
                "Develop new features and services.",
                "Comply with applicable laws, regulations, legal processes, and lawful governmental requests."
              ].map((item, index) => (
                <Typography
                  component="li"
                  key={index}
                  sx={{
                    ...bodySx,
                    mb: 1.25
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>

          {/* 5 */}
          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              5. Public Information and User Content
            </Typography>

            <Typography sx={bodySx}>
              Colloquia is a social platform. Information that you
              intentionally publish publicly may be visible to other
              Colloquia users and may be copied, shared, or otherwise
              redistributed by people who can access it.
            </Typography>

            <Typography sx={bodySx}>
              You should therefore exercise care when deciding what
              information to include in public posts, comments, photographs,
              your profile, or other publicly accessible content.
            </Typography>

            <Typography sx={bodySx}>
              We cannot guarantee that information you voluntarily make
              public will remain private after it has been published.
            </Typography>
          </Box>

          {/* 6 */}
          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              6. Cookies and Similar Technologies
            </Typography>

            <Typography sx={bodySx}>
              Colloquia may use cookies, browser storage, session
              technologies, and similar mechanisms to maintain sessions,
              remember preferences, authenticate users, improve security,
              and understand how the platform is used.
            </Typography>

            <Typography sx={bodySx}>
              Some technologies may be essential to the operation of the
              service and therefore cannot be disabled without affecting
              functionality.
            </Typography>
          </Box>

          {/* 7 */}
          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              7. How We Share Information
            </Typography>

            <Typography sx={bodySx}>
              We do not sell your personal information simply because you
              create or maintain a Colloquia account.
            </Typography>

            <Typography sx={bodySx}>
              We may disclose information to service providers and
              contractors who help us operate Colloquia. These providers
              may assist with infrastructure, hosting, authentication,
              email delivery, storage, analytics, security, or other
              services necessary to operate the platform.
            </Typography>

            <Typography sx={bodySx}>
              We may also disclose information when reasonably necessary
              to comply with law, protect the rights or safety of Colloquia
              or others, investigate abuse or fraud, enforce our agreements,
              or protect the security and integrity of the platform.
            </Typography>
          </Box>

          {/* 8 */}
          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              8. Security
            </Typography>

            <Typography sx={bodySx}>
              We take reasonable technical and organizational measures to
              protect information entrusted to Colloquia against
              unauthorized access, alteration, disclosure, or destruction.
            </Typography>

            <Typography sx={bodySx}>
              However, no method of transmitting information over the
              Internet or storing information electronically can be
              guaranteed to be completely secure.
            </Typography>
          </Box>

          {/* 9 */}
          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              9. Data Retention
            </Typography>

            <Typography sx={bodySx}>
              We retain information for as long as reasonably necessary to
              provide the service, maintain security, comply with legal
              obligations, resolve disputes, enforce agreements, and
              otherwise operate Colloquia.
            </Typography>

            <Typography sx={bodySx}>
              When information is no longer reasonably necessary for these
              purposes, we may delete or anonymize it, subject to technical,
              legal, and operational requirements.
            </Typography>
          </Box>

          {/* 10 */}
          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              10. Your Choices
            </Typography>

            <Typography sx={bodySx}>
              Depending on the functionality available to you, you may be
              able to update or correct information associated with your
              account through Colloquia.
            </Typography>

            <Typography sx={bodySx}>
              You may also contact us regarding questions about your
              personal information or requests concerning your account.
            </Typography>
          </Box>

          {/* 11 */}
          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              11. Children's Privacy
            </Typography>

            <Typography sx={bodySx}>
              Colloquia is not intended for children under the age of 13.
              We do not knowingly collect personal information from
              children under 13. If you believe that a child under 13 has
              provided personal information to Colloquia, please contact
              us so that we can investigate and take appropriate action.
            </Typography>
          </Box>

          {/* 12 */}
          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              12. Third-Party Services
            </Typography>

            <Typography sx={bodySx}>
              Colloquia may rely on third-party services to provide
              infrastructure and functionality. Those services may process
              information on our behalf or collect information pursuant to
              their own privacy policies.
            </Typography>

            <Typography sx={bodySx}>
              We encourage users to review the privacy practices of
              third-party services when appropriate.
            </Typography>
          </Box>

          {/* 13 */}
          <Box sx={{ mb: 7 }}>
            <Typography sx={sectionTitleSx}>
              13. Changes to This Privacy Policy
            </Typography>

            <Typography sx={bodySx}>
              We may update this Privacy Policy from time to time as
              Colloquia develops, our practices change, or legal
              requirements evolve.
            </Typography>

            <Typography sx={bodySx}>
              When we make changes, we will update the “Last updated” date
              at the top of this page. Your continued use of Colloquia
              after an updated Privacy Policy becomes effective constitutes
              your acknowledgment of the revised policy.
            </Typography>
          </Box>

          {/* 14 */}
          <Box sx={{ mb: 8 }}>
            <Typography sx={sectionTitleSx}>
              14. Contact Us
            </Typography>

            <Typography sx={bodySx}>
              If you have questions, concerns, or requests regarding this
              Privacy Policy or the handling of your information, please
              contact Colloquia through our support or contact channels.
            </Typography>

            <Typography
              sx={{
                fontSize: "1rem",
                lineHeight: 1.8,
                fontWeight: 300,
                color: PRIVACY_WHITE
              }}
            >
              Colloquia
            </Typography>

            <Typography
              sx={{
                fontSize: "0.95rem",
                lineHeight: 1.8,
                fontWeight: 300,
                color: PRIVACY_GRAY
              }}
            >
              From Penneferies Squire Capital
            </Typography>
          </Box>

          {/* FOOTER NAVIGATION */}
          <Box
            sx={{
              borderTop: "1px solid #222",
              pt: 4,
              display: "flex",
              flexWrap: "wrap",
              gap: {
                xs: 1,
                md: 2
              }
            }}
          >
            {[
              ["About", "/about"],
              ["Terms", "/terms"],
              ["Privacy", "/privacy"],
              ["Careers", "/careers"],
              ["Support", "/support"],
              ["Contact", "/contact"]
            ].map(([label, path]) => (
              <Button
                key={label}
                onClick={() => history.push(path)}
                sx={{
                  color:
                    label === "Privacy"
                      ? PRIVACY_WHITE
                      : PRIVACY_MUTED,
                  textTransform: "none",
                  fontWeight: 300,
                  fontSize: "0.9rem",
                  minWidth: "auto",
                  px: 1,
                  "&:hover": {
                    color: PRIVACY_WHITE,
                    bgcolor: "transparent"
                  }
                }}
              >
                {label}
              </Button>
            ))}
          </Box>

          <Typography
            sx={{
              mt: 3,
              color: "#555",
              fontSize: "0.8rem",
              fontWeight: 300
            }}
          >
            © 2026 Colloquia from Penneferies Squire Capital
          </Typography>
        </Box>
      </Box>
    </>
  );
}

export default PrivacyPage;